import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import Image from 'components/Basic/Image/Image';
import ProductAction from 'components/Blocks/Product/Action/ProductAction';
import ProductAvailableStoresCount, {
    getProductAvailabilityEShopTwClass,
    productAvailabilityTwClass,
} from 'components/Blocks/Product/Availability/ProductAvailableStoresCount';
import { ProductStock } from 'components/Blocks/Product/Availability/ProductStock';
import ProductFlags from 'components/Blocks/Product/Flags/ProductFlags';
import { ProductCatnum } from 'components/Blocks/Product/List/ListedItem/ProductCatnum';
import { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import { B2bProductPrice } from 'components/Blocks/Product/Price/B2bProductPrice';
import ProductPrice from 'components/Blocks/Product/Price/ProductPrice';
import SimpleColorVariants from 'components/Blocks/Product/SimpleColorVariants/SimpleColorVariants';
import Unwatch from 'components/Blocks/Product/Unwatch/Unwatch';
import { ProductWishlistButton } from 'components/Blocks/Product/Wishlist/ProductWishlistButtons';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { isB2bDomain, isB2cDomain, isWebDomain } from 'helpers/isDomainLocale';
import { useCurrentUserData } from 'hooks/user/useCurrentUserData';
import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum } from 'types/availability';
import { GtmListNameType } from 'types/gtm';
import { ListedProductType } from 'types/product';
import { onClickProductDetailGtmEventHandler } from 'utils/Gtm/EventHandlers';

type ProductItemProps = {
    onUnwatch?: () => void;
    gtmListName: GtmListNameType;
    listIndex: number;
    product: ListedProductType;
    type: ProductListTypes;
    inArticle?: boolean;
    underline?: boolean;
    shouldRedirectOnClick?: boolean;
    onProductItemClick?: (product: ListedProductType) => void;
    isConfigurator?: boolean;
    className?: string;
    isProductInWishlist: boolean;
    toggleProductInWishlist: () => void;
};

const ProductItem: FC<ProductItemProps> = ({
    onUnwatch,
    gtmListName,
    listIndex,
    product,
    type,
    inArticle,
    underline,
    onProductItemClick,
    shouldRedirectOnClick = true,
    isConfigurator,
    className,
    isProductInWishlist,
    toggleProductInWishlist,
}) => {
    const testIdentifier = 'blocks-product-list-listeditem-' + product.catalogNumber;
    const domainConfig = useDomainConfig();
    const { t } = useTranslation();

    const { isUserLoggedIn } = useCurrentUserData();
    const isActionPrice = product.price.priceWithoutVat < product.prices.basic.priceWithoutVat;
    const isElectronicGiftVoucher = product.isElectronicGiftVoucher;

    const onProductDetailRedirectHandler = useCallback(
        async (product: ListedProductType, listName: GtmListNameType, index: number) => {
            await onClickProductDetailGtmEventHandler(product, listName, index, domainConfig.url);
        },
        [domainConfig.url],
    );

    return (
        <div
            data-testid={testIdentifier}
            className={twJoin(
                'relative flex flex-col rounded-md text-left',
                'hover:no-underline hover:lg:z-above hover:lg:bg-white hover:lg:shadow-grayShadow hover:lg:[&>img]:mix-blend-multiply',
                underline &&
                    'after:pointer-events-none after:absolute after:-bottom-5 after:-left-[6px] after:-right-[6px] after:h-[1px] after:bg-grayLighter after:content-[""] after:lg:-bottom-2',
                inArticle && 'h-auto',
                '[-webkit-transform:translate3d(0,0,0)]', // Safari box-shadow on hover fix
                className,
            )}
            onClick={() => onProductItemClick?.(product)}
        >
            <div className="z-above -mt-7 mr-4 flex translate-y-12 justify-end">
                <ProductWishlistButton
                    isProductInWishlist={isProductInWishlist}
                    toggleProductInWishlist={toggleProductInWishlist}
                />
            </div>
            <ExtendedNextLink
                className="relative flex h-full flex-col justify-between no-underline hover:no-underline lg:px-5 lg:py-4 vl:px-6 [&:not(:last-child)]:pb-0"
                href={product.slug}
                shouldRedirectOnClick={shouldRedirectOnClick}
                type="product"
                onClick={() => onProductDetailRedirectHandler(product, gtmListName, listIndex)}
            >
                <>
                    <div>
                        <div className="relative mx-auto mb-1 flex h-[150px] w-full max-w-[160px] items-center justify-center bg-transparent text-[0px] lg:h-[200px]">
                            <Image
                                alt={t('Image of ') + product.fullName}
                                className="mix-blend-multiply"
                                height="160"
                                src={product.mainImage?.url ?? null}
                                width="160"
                            />
                        </div>
                        <SimpleColorVariants colorVariants={product.colorVariants} />
                        <div className="mb-2 flex min-h-[28px] flex-row flex-wrap items-start gap-1">
                            <ProductFlags flags={product.flags} isProductDetail={false} product={product} />
                        </div>

                        <h3
                            className={twJoin(
                                'block overflow-hidden text-body font-normal leading-6 tracking-wider text-black [word-wrap:break-word]',
                                !isB2bDomain(domainConfig) && 'mb-4',
                            )}
                        >
                            {product.fullName}
                        </h3>
                        <ProductCatnum catalogNumber={product.catalogNumber} />
                    </div>
                    {((product.prices.tescoma && isUserLoggedIn === false) || isActionPrice) &&
                        isB2cDomain(domainConfig) && (
                            <div className="mt-auto text-small">
                                {product.prices.tescoma && isUserLoggedIn === false && (
                                    <ProductPrice {...product.prices.tescoma} isTescomaPrice />
                                )}
                                {isActionPrice && <ProductPrice {...product.prices.basic} isOldPrice />}
                            </div>
                        )}
                    <div data-id="productBottom">
                        {!isWebDomain(domainConfig) && (
                            <>
                                {isB2bDomain(domainConfig) ? (
                                    <>
                                        <B2bProductPrice
                                            b2bPaymentDiscount={product.b2bPaymentDiscount}
                                            basePrice={product.prices.basic}
                                            isUnitPrice={(product.b2bMinimumProductOrder ?? 0) > 1}
                                            sellingPrice={product.price}
                                            variant="list"
                                        />
                                        <ProductStock
                                            minimumSellingQuantity={product.b2bMinimumProductOrder ?? 1}
                                            quantity={product.stockQuantity}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <ProductPrice {...product.price} isActionPrice={isActionPrice} />
                                        <div className={productAvailabilityTwClass}>
                                            <span
                                                className={getProductAvailabilityEShopTwClass(
                                                    product.availabilityStatus === AvailabilityStatusEnum.inStock,
                                                )}
                                            >
                                                {product.availability.status === AvailabilityStatusEnum.inStock
                                                    ? t('Available in eshop')
                                                    : t('Unavailable in eshop')}
                                            </span>
                                            {!isElectronicGiftVoucher && (
                                                <ProductAvailableStoresCount
                                                    availableStoresCount={product.availableStoresCount}
                                                    deliveryDaysFromCentralStore={product.deliveryDaysFromCentralStore}
                                                    isMainVariant={product.isMainVariant}
                                                    isInCentralStore={
                                                        product.availabilityStatus === AvailabilityStatusEnum.inStock
                                                    }
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </>
            </ExtendedNextLink>

            {type === ProductListTypes.Normal && (
                <ProductAction
                    gtmListName={gtmListName}
                    isConfigurator={isConfigurator}
                    listIndex={listIndex}
                    product={product}
                />
            )}
            {type === ProductListTypes.WatchDog && onUnwatch !== undefined && <Unwatch onUnwatch={onUnwatch} />}
        </div>
    );
};

export default ProductItem;
